import { FileUploader } from "react-drag-drop-files"
import Spinner from "~/components/ui/Spinner"
import Alert from "~/components/ui/Alert"
import Accordian from "~/components/ui/Accordian"
import { useState } from "react"
import { useCamp } from "~/api2/hooks/useCamp"
import { useReservationImport, ReservationImportResult } from "~/api2/hooks/useReservationImport"
import { ArrowRightIcon } from "@heroicons/react/20/solid"
import formatDateRange from "~/utils/admin/formatDateRange"

type ImportResult = ReservationImportResult

const ReservationImport = () => {
  const {
    index: { data: camp },
  } = useCamp()
  const importMutation = useReservationImport()
  const [result, setResult] = useState<ImportResult | null>(null)

  const handleImport = async (file: File) => {
    if (!camp?.id) return

    setResult(null)
    try {
      const data = await importMutation.mutateAsync({ campId: camp.id, file })
      setResult(data)
    } catch (err) {
      // error handled in hook
    }
  }

  return (
    <div className="space-y-6">
      <div className="my-6 space-y-2">
        <div className="text-xl font-semibold text-gray-900">Reservation Import</div>
        <div className="font-medium text-gray-500">
          Upload a CSV file to import reservations into your camp. The CSV should contain the necessary fields for each
          reservation.
          <br />
          <br />
          Only Campspot exports are supported at this time.
        </div>
      </div>
      {camp?.name && (
        <Alert
          kind="info"
          className="mb-4"
          description={
            <>
              You are importing reservations into <strong>{camp.name}</strong>
            </>
          }
        />
      )}
      <div>
        <FileUploader
          handleChange={handleImport}
          name="csv"
          types={["CSV"]}
          multiple={false}
          children={
            <div className="border-2 border-dashed border-gray-300 p-12 rounded-md text-center cursor-pointer hover:bg-gray-75">
              {importMutation.isPending ? (
                <div className="flex justify-center">
                  <Spinner size={8} />
                </div>
              ) : (
                <span className="text-gray-600 text-md">Click or drag a CSV file here to import reservations</span>
              )}
            </div>
          }
        />
      </div>

      {result && (
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 rounded-md bg-gray-50 p-4 text-center">
              <div className="text-sm font-medium text-gray-500">Total rows processed</div>
              <div className="mt-1 text-xl font-semibold text-gray-900">
                {result.created.length + result.errors.length}
              </div>
            </div>
            <div className="flex-1 rounded-md bg-gray-50 p-4 text-center">
              <div className="text-sm font-medium text-gray-500">Rows succeeded</div>
              <div className="mt-1 text-xl font-semibold text-gray-900">{result.created.length}</div>
            </div>
            <div className="flex-1 rounded-md bg-gray-50 p-4 text-center">
              <div className="text-sm font-medium text-gray-500">Rows failed</div>
              <div className="mt-1 text-xl font-semibold text-gray-900">{result.errors.length}</div>
            </div>
          </div>

          <Accordian label={`Successful imports (${result.created.length})`}>
            <div className="mt-2 overflow-x-auto border border-gray-200 rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Row
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Site
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Guest
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Arrival — Departure
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Reservation ID
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {result.created.map(
                    ({
                      row,
                      reservation_id,
                      guest_first_name,
                      guest_last_name,
                      site_name,
                      arrival_date,
                      departure_date,
                      import_data
                    }) => (
                      <tr key={row}>
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{row}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                          <div className="flex items-center">
                            <span className="flex-1">{import_data.site_name}</span>
                            <ArrowRightIcon className="w-5 h-5 mr-1.5 text-grey-400" />
                            <span className="font-semibold">{site_name}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                          <span className="font-semibold">{guest_first_name} {guest_last_name}</span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                          {formatDateRange(arrival_date, departure_date)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{reservation_id}</td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </Accordian>

          <Accordian label={`Errors (${result.errors.length})`} defaultOpen>
            <div className="mt-2 overflow-x-auto border border-gray-200 rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Row
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Site
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Guest
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Arrival — Departure
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Error Message
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {result.errors.map(
                    ({ row, message, guest_first_name, guest_last_name, site_name, arrival_date, departure_date }) => (
                      <tr key={row}>
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{row}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{site_name}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                          {guest_first_name} {guest_last_name}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                          {arrival_date} — {departure_date}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">{message}</td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </Accordian>
        </div>
      )}
    </div>
  )
}

export default ReservationImport
